import { useEffect } from 'react';
import { UserDetails } from './user-details';
import { IPage } from '../../../pages/types';
import { WithRouteData } from '../../../render-routes';
import {
  sendManagerEvent,
  useUserCtxSelector,
  useUserStateMatches,
} from '../../../xstate/app.xstate';
import { NEW_USER_TEMP_ID } from '../../../xstate/user-details-xstate-helper';
import { getLoggedInUserHighestRole } from '../../../xstate/permission-details-xstate-helper';

export const UserDetailsPage = ({
  userId,
  orgId,
  isMyAccount = false,
  ...props
}: IPage & { userId: string; orgId: string; isMyAccount?: boolean }) => {
  const isInviteUser = userId === NEW_USER_TEMP_ID;
  const loadedLoggedInUserDetails = useUserStateMatches('loggedIn.loaded');
  const { assignedRoles, assignedTeams } = useUserCtxSelector((ctx) => ({
    assignedRoles: ctx.user?.assignedRoles,
    assignedTeams: ctx.user?.assignedTeams,
  }));

  const loggedInUserHighestRole = getLoggedInUserHighestRole(
    assignedRoles,
    assignedTeams
  );

  useEffect(() => {
    if (loadedLoggedInUserDetails && orgId) {
      if (isInviteUser) {
        sendManagerEvent({
          type: 'manager.addNewUser',
          orgId: orgId,
          loggedInUserHighestRole: loggedInUserHighestRole || {},
        });
      } else {
        if (userId) {
          sendManagerEvent({
            type: 'manager.loadUserDetails',
            orgId: orgId,
            userId: userId,
            loggedInUserHighestRole: loggedInUserHighestRole || {},
          });
        }
      }
    }
  }, [
    userId,
    orgId,
    isInviteUser,
    loggedInUserHighestRole,
    loadedLoggedInUserDetails,
  ]);

  useEffect(() => {
    // On `/organization/:org_id/users/:user_id` the route sync selects the user
    // for us. My Account (PSTUDIO-4095) has no `user_id` segment, and the route
    // sync only ever advances the selection to a user the URL names
    // (`shouldSelectUserFromRoute`), so this surface selects its own user.
    // `ctx.selectedUser` is what `autoSaveUserDetails`,
    // `toggleAllOrgCollapseState` and primary-nav's `isCurrentUser` read; the
    // unmount below clears it, on both surfaces alike.
    if (isMyAccount && userId) {
      sendManagerEvent({ type: 'manager.selectUser', id: userId });
    }
  }, [isMyAccount, userId]);

  useEffect(() => {
    return () => {
      sendManagerEvent({
        type: 'manager.userDetails.discardUpdates',
        userId: userId,
      });
      sendManagerEvent({
        type: 'manager.clearSelectedUser',
      });
    };
  }, [userId]);

  return (
    <UserDetails
      {...props}
      isInviteUser={isInviteUser}
      userId={userId}
      orgId={orgId}
      isMyAccount={isMyAccount}
    />
  );
};

export default WithRouteData(UserDetailsPage);
